import { useQuery } from '@tanstack/react-query';
import type { UsageSummary } from '@storeflare/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Page } from '@/components/Page';
import { api } from '@/lib/api';

export function DashboardPage() {
  const usage = useQuery({
    queryKey: ['usage'],
    queryFn: () => api<UsageSummary>('/v1/usage'),
  });

  return (
    <Page title="Dashboard" subtitle="Storage, uploads, and transform usage at a glance.">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Stat label="Storage" value={formatBytes(usage.data?.storage_bytes ?? 0)} loading={usage.isLoading} />
        <Stat
          label="Uploads"
          value={(usage.data?.upload_count ?? 0).toLocaleString()}
          loading={usage.isLoading}
        />
        <Stat
          label="Transform calls"
          value={(usage.data?.transform_calls ?? 0).toLocaleString()}
          loading={usage.isLoading}
        />
      </div>
    </Page>
  );
}

function Stat({ label, value, loading }: { label: string; value: string; loading?: boolean }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription className="text-xs uppercase tracking-wide">{label}</CardDescription>
        {loading ? (
          <Skeleton className="h-7 w-24" />
        ) : (
          <CardTitle className="text-2xl">{value}</CardTitle>
        )}
      </CardHeader>
      <CardContent className="pt-0" />
    </Card>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}
